import { ref, type Ref } from 'vue';

import {
  createMessageWindow,
  MessageWindowBoundary,
  type MessageInfoSchemaType,
  type MessageWindowBoundaries,
} from '@/entities/message/model';
import type { Chat } from '@/shared/api';

type GenericChat = { id: Chat['id'] };

const useMessagesListsCache = <TChat extends GenericChat>(maxChats: number) => {
  type HistoryState = {
    error: string | null;
    hasMore: boolean;
    hasNewer: boolean;
    isInitialLoaded: boolean;
    isLoadingInitial: boolean;
    isLoadingOlder: boolean;
    isLoadingNewer: boolean;
    isRefreshingLatest: boolean;
    messageWindow: ReturnType<typeof createMessageWindow>;
    messages: MessageInfoSchemaType[];
    newerCursor: string | null;
    nextCursor: string | null;
  };
  type Cache = Map<TChat['id'], HistoryState>;

  const cached = ref<Cache>(new Map()) as Ref<Cache>;

  const getState = (chatId: TChat['id']): HistoryState | undefined => cached.value.get(chatId);

  const removeOldestStates = (): void => {
    while (cached.value.size > maxChats) {
      const oldestChatId = cached.value.keys().next().value as TChat['id'] | undefined;
      if (!oldestChatId) {
        return;
      }

      cached.value.delete(oldestChatId);
    }
  };

  const touchState = (chatId: TChat['id']): void => {
    const state = getState(chatId);
    if (!state) {
      return;
    }

    cached.value.delete(chatId);
    cached.value.set(chatId, state);
  };

  const createStateIfNotExists = (chatId: TChat['id']): HistoryState => {
    const currentState = getState(chatId);
    if (currentState) {
      touchState(chatId);
      return currentState;
    }

    const nextState: HistoryState = {
      error: null,
      hasMore: true,
      hasNewer: false,
      isInitialLoaded: false,
      isLoadingInitial: false,
      isLoadingOlder: false,
      isLoadingNewer: false,
      isRefreshingLatest: false,
      messageWindow: createMessageWindow(),
      messages: [],
      newerCursor: null,
      nextCursor: null,
    };
    cached.value.set(chatId, nextState);
    removeOldestStates();

    return nextState;
  };

  const getFromCache = (chatId: TChat['id']): MessageInfoSchemaType[] =>
    getState(chatId)?.messages ?? [];

  const synchronizeMessages = (state: HistoryState): void => {
    state.messages = state.messageWindow.snapshot().messages;
  };

  const setToCache = (
    chatId: TChat['id'],
    messages: MessageInfoSchemaType | MessageInfoSchemaType[],
  ): void => {
    const state = createStateIfNotExists(chatId);
    (Array.isArray(messages) ? messages : [messages]).forEach((message) => {
      state.messageWindow.upsertServer(message);
    });
    synchronizeMessages(state);
  };

  const updateToCache = (
    chatId: TChat['id'],
    messages: MessageInfoSchemaType | MessageInfoSchemaType[],
  ): void => {
    const state = getState(chatId);
    if (!state) {
      return;
    }

    touchState(chatId);

    (Array.isArray(messages) ? messages : [messages]).forEach((message) => {
      state.messageWindow.updateServer(message);
    });
    synchronizeMessages(state);
  };

  const replaceInCache = (
    chatId: TChat['id'],
    previousID: MessageInfoSchemaType['id'],
    message: MessageInfoSchemaType,
  ): void => {
    const state = getState(chatId);
    if (!state) {
      return;
    }

    state.messageWindow.removeServer(previousID);
    state.messageWindow.upsertServer(message);
    synchronizeMessages(state);
  };

  const removeFromCache = (chatId: TChat['id'], messageID: MessageInfoSchemaType['id']): void => {
    const state = getState(chatId);
    if (!state) {
      return;
    }

    state.messageWindow.removeServer(messageID);
    synchronizeMessages(state);
  };

  const setPagination = (
    chatId: TChat['id'],
    { hasMore, nextCursor }: Pick<HistoryState, 'hasMore' | 'nextCursor'>,
  ): void => {
    const state = createStateIfNotExists(chatId);
    state.hasMore = hasMore;
    state.nextCursor = nextCursor;
  };

  const setNewerPagination = (
    chatId: TChat['id'],
    { hasNewer, newerCursor }: Pick<HistoryState, 'hasNewer' | 'newerCursor'>,
  ): void => {
    const state = createStateIfNotExists(chatId);
    state.hasNewer = hasNewer;
    state.newerCursor = newerCursor;
  };

  const replaceServerWindow = (
    chatId: TChat['id'],
    messages: MessageInfoSchemaType[],
    boundaries: MessageWindowBoundaries,
  ): void => {
    const state = createStateIfNotExists(chatId);
    state.messageWindow.replaceServerWindow(messages, boundaries);
    state.hasMore = boundaries[MessageWindowBoundary.OLDER].hasMore;
    state.nextCursor = boundaries[MessageWindowBoundary.OLDER].cursor;
    state.hasNewer = boundaries[MessageWindowBoundary.NEWER].hasMore;
    state.newerCursor = boundaries[MessageWindowBoundary.NEWER].cursor;
    synchronizeMessages(state);
  };

  const setLoading = (
    chatId: TChat['id'],
    loading: Partial<Pick<HistoryState, 'isLoadingInitial' | 'isLoadingOlder' | 'isLoadingNewer'>>,
  ): void => {
    Object.assign(createStateIfNotExists(chatId), loading);
  };

  const setRefreshingLatest = (chatId: TChat['id'], isRefreshingLatest: boolean): void => {
    createStateIfNotExists(chatId).isRefreshingLatest = isRefreshingLatest;
  };

  const setInitialLoaded = (chatId: TChat['id'], isInitialLoaded: boolean): void => {
    createStateIfNotExists(chatId).isInitialLoaded = isInitialLoaded;
  };

  const setError = (chatId: TChat['id'], error: string | null): void => {
    createStateIfNotExists(chatId).error = error;
  };

  return {
    getState,
    touchState,
    createStateIfNotExists,
    getFromCache,
    setToCache,
    updateToCache,
    replaceInCache,
    removeFromCache,
    setPagination,
    setNewerPagination,
    replaceServerWindow,
    setLoading,
    setRefreshingLatest,
    setInitialLoaded,
    setError,
  };
};

export { useMessagesListsCache };
